import { useEffect, useRef, useState } from 'react'
import { PROPERTY_TYPES } from '@ofself/shell'
import { api } from './api.js'

/* ═══ A WORD'S OWN PROPERTIES, BEYOND THE KIT'S (owner call 2026-10-04) ═════
   The kit mints Text (multi-line already), Number, Select, Date, Checkbox and
   URL. A word wants two more, which the kit's ledger takes through `types` +
   `renderValue` rather than an app-shaped branch:
     · CODE — pseudo-code, a formula, a rule: multi-line, set in the mono face,
       tabs kept, never wrapped into prose.
     · REFERENCE — another node: one of your words, a note in Ink, a person in
       People. The value is { id, title, kind, href? } — the node by id, its
       title as a mirror, and the door to it — so any app reading the word
       reads the reference without asking this one. */
export const WORD_PROPERTY_TYPES = [
  ...PROPERTY_TYPES,
  { type: 'code', label: 'Code', glyph: '{}', empty: '' },
  { type: 'ref', label: 'Reference', glyph: '↳', empty: null },
]

function CodeValue({ value, commit, readOnly }) {
  const ref = useRef(null)
  const fit = () => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px` } }
  useEffect(fit, [value])
  return <textarea ref={ref} className="prop-input dt-prop-code" rows={1} spellCheck={false} placeholder="Empty" value={value ?? ''} disabled={readOnly}
    onChange={e => { commit({ value: e.target.value }); fit() }}
    // Tab indents, as in any editor that takes code; Shift+Tab leaves the field
    onKeyDown={e => {
      if (e.key !== 'Tab' || e.shiftKey) return
      e.preventDefault()
      // typed in as if by hand: the caret and the field's own undo stay right
      if (!document.execCommand?.('insertText', false, '  ')) {
        const el = e.currentTarget, a = el.selectionStart, b = el.selectionEnd, v = el.value
        commit({ value: `${v.slice(0, a)}  ${v.slice(b)}` })
      }
    }} />
}

const KIND_LABEL = { word: 'word', note: 'note', person: 'person' }
function RefValue({ value, commit, readOnly, words, onOpen }) {
  const [q, setQ] = useState('')
  const [found, setFound] = useState([])
  const [missing, setMissing] = useState([])
  useEffect(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) { setFound([]); return undefined }
    const mine = words.filter(w => w.name.trim() && !w.owner && w.name.toLowerCase().includes(needle)).slice(0, 6)
      .map(w => ({ id: w.id, title: w.name, kind: 'word' }))
    setFound(mine)
    // notes and people are the server's to find; a beat after the last key
    const t = setTimeout(() => api.refs(needle).then(r => { setFound(f => [...f.filter(x => x.kind === 'word'), ...(r.refs || [])]); setMissing(r.missing || []) }).catch(() => {}), 220)
    return () => clearTimeout(t)
  }, [q, words])

  if (value && value.id) {
    const open = value.kind === 'word' ? () => onOpen?.(value.id) : null
    return <span className="dt-prop-ref">
      {value.href && value.kind !== 'word'
        ? <a className="ofself-prop-chip ofself-prop-chip--door" href={value.href} target="_blank" rel="noopener noreferrer">{value.title}</a>
        : <button type="button" className="ofself-prop-chip ofself-prop-chip--door" onClick={open || undefined} disabled={!open}>{value.title}</button>}
      <span className="dt-prop-ref__kind">{KIND_LABEL[value.kind] || value.kind}</span>
      {!readOnly && <button type="button" className="ofself-prop-chip__x" aria-label={`Clear ${value.title}`} onClick={() => commit({ value: null })}>×</button>}
    </span>
  }
  if (readOnly) return null
  return <span className="dt-prop-ref dt-links__add">
    <input className="prop-input" placeholder="Find a word, a note, a person…" value={q} onChange={e => setQ(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter' && found[0]) { e.preventDefault(); commit({ value: found[0] }); setQ('') } }} />
    {q.trim() && <span className="dt-pick" role="listbox" aria-label="Nodes to point to">
      {found.map(r => <button key={`${r.kind}:${r.id}`} type="button" role="option" className="dt-pick__row"
        onMouseDown={e => e.preventDefault()} onClick={() => { commit({ value: r }); setQ('') }}>
        {r.title}<span className="dt-pick__in">{KIND_LABEL[r.kind] || r.kind}</span>
      </button>)}
      {!found.length && <span className="dt-pick__row dt-pick__none">Nothing by that name{missing.includes('person') ? ' (People needs the dictionary re-authorized)' : ''}</span>}
    </span>}
  </span>
}

/* the kit's ledger asks this for every minted property's value; undefined means "the kit's own" */
export const wordPropertyValue = ({ words, onOpen }) => (prop, { commit, readOnly }) => {
  if (prop.type === 'code') return <CodeValue value={prop.value} commit={commit} readOnly={readOnly} />
  if (prop.type === 'ref') return <RefValue value={prop.value} commit={commit} readOnly={readOnly} words={words} onOpen={onOpen} />
  // a URL reads as a link to follow, as well as a field to edit
  if (prop.type === 'url' && !readOnly && prop.value) return <span className="dt-prop-url">
    <input type="url" className="prop-input" placeholder="Empty" value={prop.value} onChange={e => commit({ value: e.target.value })} />
    <a className="dt-prop-url__go" href={prop.value} target="_blank" rel="noopener noreferrer" aria-label="Open the link">↗</a>
  </span>
  return undefined
}
